import { useQuery } from 'react-query';

import { getTrackedItems as queryFn } from 'api/endPoints/getTrackedItems';
import { QueryKeys } from 'api/queryKeys';

import { IFilter } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';

export const useQueryGetSummaryTrackedActivities = (params: QueryParams) => {
    return useQuery<QueryResponse, QueryError, QueryResponse, [QueryKeys, unknown]>(
        ['summaryTrackedActivitiesTableData', params],
        () => queryFn(params),
        {
            enabled: Boolean(params.compareVersionId) && Boolean(params.latestVersionId),
        },
    );
};

export interface QueryParams {
    projectId: number;
    latestVersionId: number;
    compareVersionId: number;
    contractId: number;
    filter: {
        filters: IFilter[];
    };
}

export type QueryResponse = MyTrackedActivitiesResponse;

interface MyTrackedActivitiesResponse {
    title: string;
    periodItemsList: PeriodItem[];
    info: IToolTip;
    typeId: string;
}

export interface PeriodItem {
    id: string;
    summary: Summary;
    itemsList: PeriodItemEntry[];
}

export interface Summary {
    id: string;
    title: string;
    startDate: number; // Unix timestamp (ms)
    endDate: number; // Unix timestamp (ms)
    items: number;
    percent: number;
}

export interface PeriodItemEntry {
    order: number;
    latestVersion: ActivityVersion;
    comparedVersion: ActivityVersion | null;
    changeMap: ChangeMap | null;
}

export interface ActivityVersion {
    predecessors: number;
    successors: number;
    startDate: number; // Unix timestamp (ms)
    finishDate: number; // Unix timestamp (ms)
    cardBoardViewResponse: CardBoardViewResponse;
    id: number;
    activityStatus: string | null;
    taskVersionHashCode: string;
    trackedActivity: boolean;
    activityId: string;
    activityName: string;
    criticalityScore: number;
    float: number | null;
    cardDetails: any;
    duration: number;
}

export interface CardBoardViewResponse {
    id: number;
    metaDataId: number;
    criticalityScore: number;
    float: number | null;
    taskHashCode: string;
    taskVersionHashCode: string;
    plannedStartDate: number;
    plannedFinishDate: number;
    actualStartDate: number | null;
    actualFinishDate: number | null;
    numOfPredecessors: number;
    numOfSuccessors: number;
    projectWeekStart: number;
    projectWeekFinish: number;
    projectMonthStart: number;
    projectMonthFinish: number;
    status: Status;
    originalStatus: Status;
    taskActivityId: string;
    parentActivity: string;
    grandParentActivity: string;
    plannedDurationInDays: number;
    actualDurationInDays: number;
    remainingDurationInDays: number;
    completedDurationInDays: number;
    taskActivityType: string;
    isVirtual: boolean;
    category: Category;
    title: string;
    startDate: number;
    endDate: number;
    dueDate: number | null;
    duration: number;
    rank: number;
    tagList: any[];
    versionDate: number;
    isStartOverdue: boolean;
    isFinishOverdue: boolean;
    taskId: number;
    startVariance: number;
    finishVariance: number;
    currentDurationOverrun: number;
    completedDurationOverrun: number;
    isInterimChanged: boolean;
    severity: any | null;
    overrunPercent: number | null;
    assignee: Assignee;
    details: string;
    cardLastComment: CardLastComment | null;
    cardIndicators: CardIndicators;
    submittedDurationComplete: number;
    isTracked: boolean;
    precedingActivities: number;
    succeedingActivites: number;
    precedingRisk: string;
    succeedingRisk: string;
    type: Category;
    baselineStartDate: number;
    baselineFinishDate: number;
    orderInPage: number | null;
    uniqId: string | null;
    isGenAiDetails: boolean;
    isStatusOrDatesChanged: boolean;
    allBudgetResourceUnit: number;
    allActualResourceUnit: number;
    allResourceNameList: string[];
    comparedBudgetResourceUnit: number | null;
    actualResourceUnit: number | null;
    remainingResourceUnit: number | null;
    resourceName: string | null;
    resourceType: string | null;
}

export interface Status {
    id: string;
    name: string;
    label: string;
}

export interface Category {
    id: number;
    name: string;
    label: string;
}

export interface Assignee {
    id: number;
    userName: string;
    firstName: string;
    lastName: string;
    imageName: string;
}

export interface CardLastComment {
    lastComment: string;
    lastCommentCreatedDate: number;
    lastCommentFirstUserName: string;
    lastCommentLastUserName: string;
}

export interface CardIndicators {
    isOverdue: Indicator;
    isCriticalPath: Indicator;
    isLongestPath: Indicator;
    isDelayDriver: Indicator;
    isConstraintDaySet: Indicator;
    isImportantStalled: Indicator;
    isImportantPredecessorOverdue: Indicator;
    isRcfRisk: Indicator;
    isLongDuration: Indicator;
    isImportantLag: Indicator;
    isSetBack: Indicator;
    isKcRisk: Indicator;
}

export interface Indicator {
    isIndicatorOn: boolean;
    text: string;
}

export interface ChangeMap {
    predecessors: number | null;
    successors: number | null;
    startDate: number | null;
    finishDate: number | null;
    criticalPath: boolean | null;
    criticalityScore: number | null;
    float: number | null;
    duration: number | null;
    activityStatus: string | null;
}

export interface InfoSection {
    infoList: InfoItem[];
    link: string;
}

export interface InfoItem {
    content: string;
    color: string | null;
}
